import React, { useState } from 'react';
import { useAppStateContext } from 'src/appState/AppState';
import { observer } from 'src/utils/mobx-react';
import { EventsListState } from 'src/domains/sportsbook/webview/components/eventsList/EventList.state';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { EventListWrapper } from 'src/domains/sportsbook/webview/components/eventsList/EventList.style';
import { EventsTableInfo } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsTableMobX.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { SpecialSportsState } from 'src/domains/sportsbook/state/specialSportsState/SpecialSportsState';
import { useCommon } from 'src/domains/common/Common';
import { TranslationsStore } from 'src/domains/layouts/state/translationsStore/TranslationsStore';
import { EventsListCompetitionGroup } from 'src/domains/sportsbook/webview/components/eventsList/EventsListCompetitionGroup';
import { Collapse } from 'src/ui/components/collapse/sports/02/Collapse';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

export interface EventListPropsType {
    events: EventsCollectionQueryModel;
    filterMarketsByPopularTag: boolean;
    showHeaderSport: boolean;
    allGroupsOpen: boolean;
    specialSportsState: SpecialSportsState | null;
}

export const EventsList = observer('EventsList', (props: EventListPropsType) => {
    const common = useCommon();
    const appState = useAppStateContext();
    const translationsStore = TranslationsStore.get(common);
    const configState = ConfigComponents.get(common).config;
    const allGroupsOpen = props.allGroupsOpen;
    const specialSportState = props.specialSportsState;

    const [state] = useState(() => new EventsListState(appState, specialSportState, allGroupsOpen));
    const { filterMarketsByPopularTag } = props;

    const renderContent = (): React.ReactNode => {
        const { showHeaderSport } = props;

        const eventsGroup = state.groupEvents.get(props.events);

        const out = [];

        if (eventsGroup.listToView.sports.length === 0) {
            out.push(
                <EventsTableInfo>
                    <I18n
                        langKey='events.table.empty'
                        defaultText="Sorry, we haven't found any events with such criteria."
                    />
                </EventsTableInfo>
            );
        }

        for (const sportGroup of eventsGroup.listToView.sports) {
            const sport = sportGroup.id;

            const headerSport = state.calculateShowFlag(showHeaderSport, sportGroup.visible);
            const sportName = translationsStore.translateSport(sport);

            const sportKey = `sport_${sport}`;

            if (headerSport.showHeader) {
                out.push(
                    <Collapse
                        key={`${sportKey}_header`}
                        dataTest='header-sportLevel'
                        isOpen={headerSport.showBody}
                        handleToggle={sportGroup.toggle}
                        title={sportName}
                        sportId={sport}
                    />
                );
            }

            if (headerSport.showBody) {
                out.push(
                    <EventsListCompetitionGroup
                        key={`${sportKey}_body`}
                        wrapperKey={`${sportKey}_body`}
                        competitions={sportGroup.competitions}
                        state={state}
                        filterMarketsByPopularTag={filterMarketsByPopularTag}
                    />
                );
            }
        }

        return out;
    };

    const renderSpecialSportContent = (): React.ReactNode => {
        const eventsGroup = state.groupEvents.get(props.events);
        const competitions = eventsGroup.specialSportListToView;

        return [
            <EventsListCompetitionGroup
                key='special_body'
                wrapperKey='special_body'
                competitions={competitions}
                state={state}
                filterMarketsByPopularTag={filterMarketsByPopularTag}
            />,
        ];
    };

    return (
        <EventListWrapper variant={configState.componentVariants.sportsbookVariant}>
            {props.specialSportsState?.isSpecial === true ? renderSpecialSportContent() : renderContent()}
        </EventListWrapper>
    );
});
